import React, { useState } from 'react';
import { PaymentTrackingUISearchResultModel } from '../../services/api/paymentsApi';

interface PdfPreviewModalProps {
  payment: PaymentTrackingUISearchResultModel;
  paymentSummary: any;
  onClose: () => void;
  onSendEmail: () => void;
}

// Format currency
const formatCurrency = (amount: number, currency: string | null): string => {
  if (amount === 0 || !currency) return '-';
  
  return new Intl.NumberFormat('en-GB', { 
    style: 'currency', 
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};

const PdfPreviewModal: React.FC<PdfPreviewModalProps> = ({ 
  payment, 
  paymentSummary, 
  onClose, 
  onSendEmail 
}) => {
  const [emailSent, setEmailSent] = useState(false);

  // Extract system reference from external payment ID
  const getSystemReference = (): string => {
    if (!payment.externalPaymentId) return '-';
    
    const parts = payment.externalPaymentId.split(',');
    return parts[parts.length - 1] || parts[0] || '-';
  };

  // Get payment message details if available
  const getPaymentDetails = (): any => {
    if (!paymentSummary || !paymentSummary.paymentMessage) return null;
    
    try {
      return JSON.parse(paymentSummary.paymentMessage);
    } catch (error) {
      console.error('Error parsing payment message:', error);
      return null;
    }
  };

  const paymentDetails = getPaymentDetails();

  // Get debtor/creditor account details
  const getAccountDetails = () => {
    if (!paymentDetails) return { debtor: {}, creditor: {} };
    
    // Extract from CreditTransferTransactionInformation
    const txnInfo = paymentDetails.CreditTransferTransactionInformation || {};
    
    return {
      debtor: {
        name: txnInfo.Debtor?.Name || '-',
        accountId: txnInfo.DebtorAccount?.Identification?.Other?.Identification || 
                  txnInfo.DebtorAccount?.Identification?.Iban || '-',
        agent: txnInfo.DebtorAgent?.FinancialInstitutionIdentification?.BICFI || '-'
      },
      creditor: {
        name: txnInfo.Creditor?.Name || '-',
        accountId: txnInfo.CreditorAccount?.Identification?.Iban || 
                  txnInfo.CreditorAccount?.Identification?.Other?.Identification || '-',
        agent: txnInfo.CreditorAgent?.FinancialInstitutionIdentification?.BICFI || '-'
      },
      endToEndId: txnInfo.PaymentIdentification?.EndToEndId || '-',
      remittanceInfo: txnInfo.RemittanceInformation?.Unstructured || 
                     (txnInfo.RemittanceInformation?.Structured ? 
                       txnInfo.RemittanceInformation.Structured.CreditorReferenceInformation?.Reference : '-')
    };
  };

  const accountDetails = getAccountDetails();

  // Handle send email
  const handleSendEmail = () => {
    onSendEmail();
    setEmailSent(true);
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
      aria-modal="true"
      aria-labelledby="pdf-modal-title"
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '4px',
          maxWidth: '90%',
          width: '800px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div 
          style={{
            padding: '16px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 id="pdf-modal-title" style={{ margin: 0 }}>Payment Proof</h3>
          <button 
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0 8px',
            }}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        
        {/* PDF Preview Content */}
        <div 
          style={{
            padding: '24px',
            overflowY: 'auto',
            maxHeight: 'calc(90vh - 130px)',
            backgroundColor: 'white',
          }}
        >
          {/* PDF Header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            marginBottom: '24px',
            borderBottom: '2px solid #003366',
            paddingBottom: '16px',
          }}>
            <div>
              <h2 style={{ 
                color: '#003366', 
                margin: 0,
                fontWeight: 'bold',
              }}>
                Payment Confirmation
              </h2>
              <p style={{ margin: '8px 0 0 0', color: '#666' }}>
                {formatDate(payment.startTime)}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <img 
                src="https://www.investec.com/content/dam/investec-website/logos/specialists-banking-logo.svg" 
                alt="Investec Logo" 
                style={{ width: '150px', height: 'auto' }}
              />
            </div>
          </div>

          {/* Payment Details */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              color: '#003366', 
              marginBottom: '16px',
              fontSize: '18px',
              borderBottom: '1px solid #eee',
              paddingBottom: '8px',
            }}>
              Payment Details
            </h3>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr',
              rowGap: '16px',
              columnGap: '24px',
            }}>
              <div>
                <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '14px' }}>System Reference</p>
                <p style={{ margin: 0, fontWeight: 'bold' }}>{getSystemReference()}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '14px' }}>End-to-End ID</p>
                <p style={{ margin: 0, fontWeight: 'bold' }}>{accountDetails.endToEndId}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '14px' }}>Amount</p>
                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '18px' }}>
                  {formatCurrency(payment.toAmount, payment.toCurrency)}
                </p>
              </div>
              <div>
                <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '14px' }}>Status</p>
                <p style={{ 
                  margin: 0, 
                  fontWeight: 'bold',
                  color: payment.paymentStatus === 'Succeeded' ? 'green' : (
                    payment.paymentStatus === 'Failed' ? 'red' : 'orange'
                  )
                }}>
                  {payment.paymentStatus}
                </p>
              </div>
              <div>
                <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '14px' }}>Payment Method</p>
                <p style={{ margin: 0 }}>{payment.paymentMethod}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '14px' }}>Date</p>
                <p style={{ margin: 0 }}>{formatDate(payment.startTime)}</p>
              </div>
              {payment.fromCurrency !== payment.toCurrency && (
                <>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '14px' }}>Original Amount</p>
                    <p style={{ margin: 0 }}>
                      {formatCurrency(payment.fromAmount, payment.fromCurrency)}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '14px' }}>FX Rate</p>
                    <p style={{ margin: 0 }}>
                      {(payment.toAmount / payment.fromAmount).toFixed(4)}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Debtor and Creditor Details */}
          <div style={{ 
            display: 'flex',
            gap: '24px',
            marginBottom: '24px',
          }}>
            {/* Debtor Details */}
            <div style={{ flex: 1 }}>
              <h3 style={{ 
                color: '#003366', 
                marginBottom: '16px',
                fontSize: '18px',
                borderBottom: '1px solid #eee',
                paddingBottom: '8px',
              }}>
                From
              </h3>
              <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{accountDetails.debtor.name}</p>
              <p style={{ margin: '0 0 4px 0' }}>Account: {accountDetails.debtor.accountId}</p>
              <p style={{ margin: '0 0 4px 0' }}>Bank: {accountDetails.debtor.agent}</p>
            </div>

            {/* Creditor Details */}
            <div style={{ flex: 1 }}>
              <h3 style={{ 
                color: '#003366', 
                marginBottom: '16px',
                fontSize: '18px',
                borderBottom: '1px solid #eee',
                paddingBottom: '8px',
              }}>
                To
              </h3>
              <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{accountDetails.creditor.name}</p>
              <p style={{ margin: '0 0 4px 0' }}>Account: {accountDetails.creditor.accountId}</p>
              <p style={{ margin: '0 0 4px 0' }}>Bank: {accountDetails.creditor.agent}</p>
            </div>
          </div>

          {/* Reference Information */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              color: '#003366', 
              marginBottom: '16px',
              fontSize: '18px',
              borderBottom: '1px solid #eee',
              paddingBottom: '8px',
            }}>
              Reference
            </h3>
            <p style={{ margin: 0 }}>{accountDetails.remittanceInfo}</p>
          </div>

          {/* Footer */}
          <div style={{ 
            marginTop: '32px', 
            paddingTop: '16px',
            borderTop: '1px solid #eee',
            fontSize: '12px',
            color: '#666',
          }}>
            <p style={{ margin: '0 0 8px 0' }}>
              This document serves as a proof of payment from Investec Bank.
            </p>
            <p style={{ margin: 0 }}>
              For any inquiries, please contact your relationship manager.
            </p>
          </div>
        </div>
        
        {/* Modal Footer */}
        <div 
          style={{
            padding: '16px',
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          <button 
            style={{
              backgroundColor: '#003366',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
              opacity: emailSent ? 0.7 : 1,
            }}
            onClick={handleSendEmail}
            disabled={emailSent}
          >
            {emailSent ? 'Email Sent' : 'Email to Client'}
          </button>
          <button 
            style={{
              backgroundColor: '#f0f0f0',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
            }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewModal; 